// ============================================
// DASHBOARD PAGE
// ============================================
// TIER-4 UPDATE:
// [ŞİRKET-H7] 30/90 günlük filtre çalışır hale getirildi
// ============================================

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Briefcase,
  FileText,
  Users,
  TrendingUp,
  Eye,
  Clock,
  CheckCircle,
  Plus,
  ArrowRight,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { PageHeader, StatCard, StatusBadge } from '@/components';
import { companiesApi, jobsApi } from '@/services/api';
import { formatRelativeTime } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/')({
  beforeLoad: () => {
    throw redirect({ to: '/jobs' });
  },
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation('panel');
  // [ŞİRKET-H7] Seçili periyot state'i
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const queryClient = useQueryClient();

  // Tüm dashboard verilerini yenile
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['company'] });
    queryClient.invalidateQueries({ queryKey: ['jobs', 'me'] });
  };

  const periodOptions = [
    { value: '7d', label: t('dashboard.last7Days') },
    { value: '30d', label: t('dashboard.last30Days') },
    { value: '90d', label: t('dashboard.last90Days') },
  ];

  // Fetch company stats
  const { data: rawStats, isLoading: statsLoading, isError: statsError } = useQuery({
    queryKey: ['company', 'stats'],
    queryFn: () => companiesApi.getStats(),
    staleTime: 1000 * 60 * 2,
    retry: false, // Hata gelince anında dur
  });

  // Handle backend response wrapper { success: true, data: {...} }
  const stats = (rawStats as any)?.data || rawStats || {};

  // Fetch recent jobs
  const { data: rawJobsData, isLoading: jobsLoading, isError: jobsError } = useQuery({
    queryKey: ['jobs', 'me', { limit: 5 }],
    queryFn: () => jobsApi.getMyJobs({ limit: 5 }),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  // Handle backend response wrapper for paginated data
  const jobsData = (rawJobsData as any)?.data || rawJobsData;

  // [ŞİRKET-H7] Fetch company analytics with selected period
  const { data: rawAnalytics, isLoading: analyticsLoading, isError: analyticsError } = useQuery({
    queryKey: ['company', 'analytics', selectedPeriod],
    queryFn: () => companiesApi.getAnalytics(selectedPeriod),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  // Handle backend response wrapper
  const analytics = (rawAnalytics as any)?.data || rawAnalytics;

  // Chart data processing - Array kontrolü ile güvenli hale getirildi
  const jobViewsArray = Array.isArray(analytics?.jobViews) ? analytics.jobViews : [];
  const applicationsArray = Array.isArray(analytics?.applications) ? analytics.applications : [];
  
  const chartData = jobViewsArray.map((item: any, index: number) => ({
    name: item.date || `${t('dashboard.day')} ${index + 1}`,
    views: item.views || 0,
    applications: applicationsArray[index]?.count || 0,
  }));

  // Fallback mock data for chart if no analytics
  const fallbackChartData = [
    { name: t('dashboard.weekDays.mon'), applications: 12, views: 45 },
    { name: t('dashboard.weekDays.tue'), applications: 8, views: 52 },
    { name: t('dashboard.weekDays.wed'), applications: 15, views: 48 },
    { name: t('dashboard.weekDays.thu'), applications: 10, views: 61 },
    { name: t('dashboard.weekDays.fri'), applications: 18, views: 55 },
    { name: t('dashboard.weekDays.sat'), applications: 6, views: 32 },
    { name: t('dashboard.weekDays.sun'), applications: 4, views: 28 },
  ];

  const displayChartData = chartData.length > 0 ? chartData : fallbackChartData;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={
          <Link to="/jobs/create" className="btn-primary">
            <Plus className="w-4 h-4" />
            {t('dashboard.createJob')}
          </Link>
        }
      />

      {/* Bağlantı Hatası Banner */}
      {statsError && (
        <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-maritime text-red-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Veriler yüklenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin.</span>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors ml-4 flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            Yenile
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title={t('dashboard.activeJobs')}
          value={stats?.activeJobs || 0}
          icon={<Briefcase className="w-6 h-6" />}
          iconColor="bg-blue-100 text-blue-600"
        />
        <StatCard
          title={t('dashboard.totalApplications')}
          value={stats?.totalApplications || 0}
          icon={<FileText className="w-6 h-6" />}
          iconColor="bg-green-100 text-green-600"
        />
        <StatCard
          title={t('dashboard.pendingApplications')}
          value={stats?.pendingApplications || 0}
          icon={<Clock className="w-6 h-6" />}
          iconColor="bg-amber-100 text-amber-600"
        />
        <StatCard
          title="Takipçiler"
          value={stats?.followerCount || 0}
          icon={<Users className="w-6 h-6" />}
          iconColor="bg-indigo-100 text-indigo-600"
        />
        <StatCard
          title={t('dashboard.profileViews')}
          value={stats?.profileViews || 0}
          icon={<Eye className="w-6 h-6" />}
          iconColor="bg-purple-100 text-purple-600"
          change={{ value: stats?.profileViewsChange || 0, label: t('dashboard.thisMonth') }}
        />
      </div>

      {/* Charts & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Application Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-maritime border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('dashboard.chartTitle', { period: selectedPeriod === '7d' ? t('dashboard.weeklyChart') : selectedPeriod === '30d' ? t('dashboard.monthlyChart') : t('dashboard.quarterlyChart') })}
            </h3>
            {/* [ŞİRKET-H7] Period selector */}
            <select 
              className="text-sm border border-gray-300 rounded-maritime px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-maritime-500"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {(analyticsLoading && !analyticsError) ? (
            <div className="h-72 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={displayChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.625rem',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name={t('dashboard.views')}
                    stroke="#8b5cf6"
                    fill="#ede9fe"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="applications"
                    name={t('dashboard.applicationCount')}
                    stroke="#0086c9"
                    fill="#e0f2fe"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Period summary */}
          {analytics && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.totalViews')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {analytics.totalViews ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.totalApps')}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {analytics.totalApplications ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.conversionRate')}</p>
                <p className="text-lg font-semibold text-green-600">
                  {analytics.totalViews && analytics.totalApplications
                    ? `${((analytics.totalApplications / analytics.totalViews) * 100).toFixed(1)}%`
                    : analytics.totalApplications > 0 ? '%100' : '-'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('dashboard.quickStats')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.shortlistedCandidates')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {stats?.shortlistedCandidates || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.acceptedCandidates')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {stats?.acceptedCandidates || stats?.hiredThisMonth || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.averageRating')}</span>
              </div>
              <span className="text-sm font-semibold text-amber-600">
                {stats?.averageRating ? `${stats.averageRating.toFixed(1)} / 5` : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{t('dashboard.totalReviews')}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {stats?.totalReviews || 0}
              </span>
            </div>
          </div>
          
          {/* Top Jobs (from analytics) */}
          {Array.isArray(analytics?.topJobs) && analytics.topJobs.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('dashboard.topViewedJobs')}</h4>
              <div className="space-y-2">
                {analytics.topJobs.slice(0, 3).map((job: any) => (
                  <div key={job.jobId} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate max-w-[150px]">{job.title}</span>
                    <span className="text-gray-900 font-medium">{t('dashboard.viewCount', { count: job.views })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="bg-white rounded-maritime border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.recentJobs')}</h3>
          <Link
            to="/jobs"
            className="text-sm text-maritime-600 hover:text-maritime-700 font-medium flex items-center gap-1"
          >
            {t('dashboard.viewAll')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {(jobsLoading && !jobsError) ? (
            <div className="p-6 text-center text-gray-500">{t('dashboard.loading')}</div>
          ) : jobsData?.data?.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {t('dashboard.noJobs')}
              <Link to="/jobs/create" className="text-maritime-600 ml-1">
                {t('dashboard.createFirstJob')}
              </Link>
            </div>
          ) : (
            jobsData?.data?.slice(0, 5).map((job: any) => (
              <div
                key={job.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to="/jobs/$slug"
                    params={{ slug: job.slug }}
                    className="text-sm font-medium text-gray-900 hover:text-maritime-600"
                  >
                    {job.title}
                  </Link>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('dashboard.applicationsCount', { count: job._count?.applications || 0 })} • {t('dashboard.createdAt')}{' '}
                    {formatRelativeTime(job.createdAt)}
                  </p>
                </div>
                <StatusBadge status={job.status} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
